import React from 'react';
import './App.css';

import QRGenerator from './QRGenerator';
import logo from './assets/logo.png';
import authorityCabNight from './assets/authority-black-cab-night.png';

function getPortal() {
  const host = window.location.hostname.toLowerCase();

  if (host.startsWith('admin.')) return 'admin';
  if (host.startsWith('driver.')) return 'driver';
  if (host.startsWith('passenger.')) return 'passenger';

  return 'main';
}

const portalContent = {
  main: {
    kicker: 'Track. Share. Arrive.',
    title: 'CabSafe',
    summary: 'UK hackney carriage journey sharing for passengers, drivers, and safety operators.',
  },
  admin: {
    kicker: 'CabSafe Operations',
    title: 'Admin Portal',
    summary: 'Monitor live journeys, review driver activity, and keep passenger safety workflows moving.',
  },
  driver: {
    kicker: 'CabSafe Drivers',
    title: 'Driver Portal',
    summary: 'Driver-only access for completing your CabSafe profile and journey QR tools.',
  },
  passenger: {
    kicker: 'CabSafe Passengers',
    title: 'Passenger Portal',
    summary: 'Open a shared journey, check trip details, and keep trusted contacts informed.',
  },
};

const driverPhotoMaxDimension = 640;
const driverPhotoUploadType = 'image/webp';
const driverPhotoUploadQuality = 0.82;

function loadImageFromFile(file) {
  const objectUrl = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to read picture'));
    };
    image.src = objectUrl;
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Unable to read picture'));
    reader.readAsDataURL(blob);
  });
}

async function prepareDriverPhotoUpload(file) {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('Use a PNG, JPG, or WEBP picture');
  }

  const image = await loadImageFromFile(file);
  const scale = Math.min(
    1,
    driverPhotoMaxDimension / Math.max(image.naturalWidth || 1, image.naturalHeight || 1)
  );
  const width = Math.max(1, Math.round((image.naturalWidth || 1) * scale));
  const height = Math.max(1, Math.round((image.naturalHeight || 1) * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to prepare picture');
  }

  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (nextBlob) => {
        if (!nextBlob) {
          reject(new Error('Unable to prepare picture'));
          return;
        }
        resolve(nextBlob);
      },
      driverPhotoUploadType,
      driverPhotoUploadQuality
    );
  });

  return blobToDataUrl(blob);
}

function App() {
  const portal = getPortal();
  const content = portalContent[portal];
  const isPublicDriverRoute = portal === 'passenger' && Boolean(getDriverProfileIdFromPath());

  return (
    <div className={`App portal-${portal}${isPublicDriverRoute ? ' public-driver-route' : ''}`}>
      {portal === 'main' && <PublicSiteHeader />}
      <main className="app-shell">
        <section className="brand-panel" aria-labelledby="brand-title">
          <img className="brand-logo" src={logo} alt="CabSafe" />
          <div className="brand-copy">
            <p className="brand-kicker">{content.kicker}</p>
            <h1 id="brand-title">{content.title}</h1>
            <p className="brand-summary">{content.summary}</p>
          </div>
        </section>

        <section className="workspace" aria-label="CabSafe tools">
          {portal === 'main' && <MainSite />}
          {portal === 'admin' && (
            isPasswordSetupPath() ? (
              <PasswordSetup />
            ) : (
              <AuthGate allowedRoles={['admin']} portalName="Admin Portal">
                <AdminPortal />
              </AuthGate>
            )
          )}
          {portal === 'driver' && (
            isPasswordSetupPath() ? (
              <PasswordSetup />
            ) : (
              <AuthGate allowedRoles={['driver']} portalName="Driver Login">
                <DriverPortal />
              </AuthGate>
            )
          )}
          {portal === 'passenger' && <PassengerPortal />}
        </section>
      </main>
    </div>
  );
}

function PublicSiteHeader() {
  return (
    <header className="site-topbar">
      <a className="site-wordmark" href="/">CabSafe</a>
      <nav className="site-nav" aria-label="CabSafe public pages">
        <a href="/local-authorities">Local authorities</a>
        <a href="/hackney-drivers">Hackney drivers</a>
      </nav>
      <details className="portal-login">
        <summary>Login</summary>
        <div className="portal-login-menu">
          <a href="https://passenger.cabsafe.taxi">Passenger portal</a>
          <a href="https://driver.cabsafe.taxi">Driver portal</a>
          <a href="https://admin.cabsafe.taxi">Admin portal</a>
        </div>
      </details>
    </header>
  );
}

function MainSite() {
  if (isLocalAuthoritiesPath()) {
    return <LocalAuthoritiesPage />;
  }

  if (isHackneyDriversPath()) {
    return <HackneyDriversPage />;
  }

  return (
    <div className="home-site">
      <section className="home-hero" aria-labelledby="home-title">
        <p className="home-kicker">Journey sharing for safer trips home</p>
        <h2 id="home-title">Share the trip. Reassure the people who care.</h2>
        <p>
          CabSafe lets a passenger share their live journey and location with a
          trusted friend or family member, then stops sharing automatically when
          they arrive home.
        </p>
      </section>

      <section className="home-strip" aria-label="CabSafe benefits">
        <div>
          <strong>Live</strong>
          <span>location sharing while the journey is active</span>
        </div>
        <div>
          <strong>Trusted</strong>
          <span>friends and family can follow progress</span>
        </div>
        <div>
          <strong>Automatic</strong>
          <span>sharing ends when the passenger gets home</span>
        </div>
      </section>

      <section className="home-flow" aria-labelledby="flow-title">
        <div className="tool-heading">
          <p>How CabSafe works</p>
          <h2 id="flow-title">A simple safety layer for the journey home</h2>
        </div>
        <div className="home-flow-grid">
          <article>
            <span>01</span>
            <h3>Start the journey</h3>
            <p>The passenger chooses where they are going and who should be able to follow.</p>
          </article>
          <article>
            <span>02</span>
            <h3>Share the route</h3>
            <p>Their trusted contact gets a live view of the trip and current location.</p>
          </article>
          <article>
            <span>03</span>
            <h3>Arrive safely</h3>
            <p>When home is reached, CabSafe ends journey sharing so tracking is temporary.</p>
          </article>
        </div>
      </section>

      <section className="home-reassurance">
        <div>
          <p>Safety without constant check-ins</p>
          <h2>CabSafe is built for reassurance, not permanent tracking.</h2>
        </div>
      </section>
    </div>
  );
}

function HackneyDriversPage() {
  return (
    <div className="driver-site">
      <section className="driver-hero" aria-labelledby="driver-title">
        <img
          className="driver-hero-cab"
          src={authorityCabNight}
          alt="Black cab on a wet city street at night"
        />
        <p className="home-kicker">For independent Hackney drivers</p>
        <h2 id="driver-title">Rebuild passenger confidence while staying independent.</h2>
        <p>
          CabSafe gives Hackney drivers the kind of safety technology passengers
          now expect from major firms, without taking away the independence,
          identity, and local trust that make licensed drivers different. Journey
          tracking cuts off when the passenger reaches their destination.
        </p>
      </section>

      <section className="authority-strip" aria-label="Hackney driver benefits">
        <div>
          <strong>Trust</strong>
          <span>Show passengers they are in a licensed, accountable, safer journey.</span>
        </div>
        <div>
          <strong>Confidence</strong>
          <span>Let riders share their route, then stop tracking automatically on arrival.</span>
        </div>
        <div>
          <strong>Independence</strong>
          <span>Keep control of your work while offering modern safety features.</span>
        </div>
      </section>

      <section className="authority-section" aria-labelledby="driver-benefits">
        <div className="tool-heading">
          <p>Why drivers use CabSafe</p>
          <h2 id="driver-benefits">Major-firm reassurance for independent licensed drivers</h2>
        </div>
        <div className="authority-grid">
          <article>
            <h3>Make passengers feel safer</h3>
            <p>
              A CabSafe journey gives passengers a simple way to share where
              they are going and reassure someone they trust until they reach
              their destination.
            </p>
          </article>
          <article>
            <h3>Strengthen your professional image</h3>
            <p>
              Your driver profile, vehicle details, and journey QR flow help
              passengers see they are travelling with a proper licensed driver.
            </p>
          </article>
          <article>
            <h3>Compete with modern platforms</h3>
            <p>
              CabSafe adds the digital confidence people associate with app
              firms while keeping the driver-passenger relationship local and direct.
            </p>
          </article>
        </div>
      </section>

      <section className="authority-section" aria-labelledby="driver-flow">
        <div className="tool-heading">
          <p>Driver workflow</p>
          <h2 id="driver-flow">Simple to explain, easy for passengers to use</h2>
        </div>
        <div className="authority-list">
          <div>
            <span>01</span>
            <p>Display your CabSafe QR code so passengers can start a shared journey from their own phone.</p>
          </div>
          <div>
            <span>02</span>
            <p>The passenger chooses their destination and shares the journey link with someone they trust.</p>
          </div>
          <div>
            <span>03</span>
            <p>The shared journey can show live progress, route information, and arrival status.</p>
          </div>
          <div>
            <span>04</span>
            <p>When the passenger reaches their destination, CabSafe cuts off tracking so the safety feature is temporary and purposeful.</p>
          </div>
        </div>
      </section>

      <section className="authority-reassurance">
        <div>
          <p>Independent, but not behind</p>
          <h2>CabSafe helps Hackney drivers offer reassurance without becoming a major app firm.</h2>
        </div>
      </section>
    </div>
  );
}

function LocalAuthoritiesPage() {
  return (
    <div className="authority-site">
      <section className="authority-hero" aria-labelledby="authority-title">
        <img
          className="authority-hero-cab"
          src={authorityCabNight}
          alt="Black cab on a wet city street at night"
        />
        <p className="home-kicker">For licensing teams and local authorities</p>
        <h2 id="authority-title">A clearer safety layer around licensed taxi journeys.</h2>
        <p>
          CabSafe gives local authorities a practical way to encourage safer
          journeys, improve public confidence, and build a better operational
          view of passenger trips without asking councils to replace existing
          licensing systems.
        </p>
      </section>

      <section className="authority-strip" aria-label="Local authority outcomes">
        <div>
          <strong>Visibility</strong>
          <span>Journey records can support licensing, safeguarding, and complaint workflows.</span>
        </div>
        <div>
          <strong>Confidence</strong>
          <span>Passengers can share a live journey with someone they trust.</span>
        </div>
        <div>
          <strong>Integration</strong>
          <span>CabSafe can sit alongside existing driver, vehicle, and case systems.</span>
        </div>
      </section>

      <section className="authority-section" aria-labelledby="authority-benefits">
        <div className="tool-heading">
          <p>Why get on board</p>
          <h2 id="authority-benefits">Support public safety with useful journey data</h2>
        </div>
        <div className="authority-grid">
          <article>
            <h3>Track journeys when it matters</h3>
            <p>
              CabSafe can record the driver, passenger journey reference,
              destination, status, timestamps, and available route/location
              updates so authorised teams have a clearer audit trail.
            </p>
          </article>
          <article>
            <h3>Improve response to concerns</h3>
            <p>
              When a passenger, family member, operator, or licensing officer
              needs to review a trip, CabSafe can provide a structured record
              rather than relying on fragmented messages and screenshots.
            </p>
          </article>
          <article>
            <h3>Encourage safer behaviours</h3>
            <p>
              Promoting CabSafe gives passengers an easy habit: start a shared
              journey, let a trusted contact follow progress, and stop sharing
              automatically once they arrive home.
            </p>
          </article>
        </div>
      </section>

      <section className="authority-section" aria-labelledby="authority-integrations">
        <div className="tool-heading">
          <p>Integration approach</p>
          <h2 id="authority-integrations">Designed to work with council systems</h2>
        </div>
        <div className="authority-list">
          <div>
            <span>01</span>
            <p>Link CabSafe driver profiles to existing licence, badge, plate, and vehicle records.</p>
          </div>
          <div>
            <span>02</span>
            <p>Expose controlled reporting views for authorised licensing, safeguarding, or enforcement teams.</p>
          </div>
          <div>
            <span>03</span>
            <p>Feed journey events into CRM, case management, data warehouse, or operator systems through API integration.</p>
          </div>
          <div>
            <span>04</span>
            <p>Use role-based access so operational users see what they need while passenger sharing stays temporary and purposeful.</p>
          </div>
        </div>
      </section>

      <section className="authority-reassurance">
        <div>
          <p>Balanced by design</p>
          <h2>CabSafe is about accountable journeys, passenger reassurance, and proportionate data use.</h2>
        </div>
      </section>
    </div>
  );
}

function AuthGate({ allowedRoles, portalName, children }) {
  const [user, setUser] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    fetch('/api/auth/me')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => setUser(data?.user || null))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return <Panel title={portalName} eyebrow="Checking access">Loading secure portal</Panel>;
  }

  if (!user || !allowedRoles.includes(user.role)) {
    return <LoginPanel portalName={portalName} onSignedIn={setUser} />;
  }

  return (
    <>
      {children}
      <button className="secondary-action" onClick={() => signOut(setUser)}>
        Sign Out
      </button>
    </>
  );
}

function LoginPanel({ portalName, onSignedIn }) {
  const isDriverLogin = portalName === 'Driver Login';
  const canResetPassword = isDriverLogin || portalName === 'Admin Portal';
  const canTrustDevice = !isDriverLogin;
  const [email, setEmail] = React.useState(() => {
    const queryEmail = new URLSearchParams(window.location.search).get('email');
    return queryEmail || (portalName === 'Admin Portal' ? 'paul@cabsafe.taxi' : '');
  });
  const [password, setPassword] = React.useState('');
  const [token, setToken] = React.useState('');
  const [setup, setSetup] = React.useState(null);
  const [requiresTotp, setRequiresTotp] = React.useState(false);
  const [trustDevice, setTrustDevice] = React.useState(true);
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [resetStatus, setResetStatus] = React.useState('');
  const [resetError, setResetError] = React.useState('');
  const [isResetSending, setIsResetSending] = React.useState(false);
  const lastAutoSubmittedToken = React.useRef('');

  const submit = async (event, submittedToken = token) => {
    event?.preventDefault();

    const authToken = String(submittedToken || '')
      .replace(/\D/g, '')
      .slice(0, 6);

    if (requiresTotp && authToken.length !== 6) {
      setError('Enter the 6 digit authenticator code');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, token: authToken, trustDevice }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to sign in');
      if (!isDriverLogin && data.requiresTotpSetup) {
        setSetup(data);
        setRequiresTotp(true);
        setToken('');
        lastAutoSubmittedToken.current = '';
        return;
      }
      if (!isDriverLogin && data.requiresTotp) {
        setRequiresTotp(true);
        setToken('');
        lastAutoSubmittedToken.current = '';
        return;
      }

      onSignedIn(data.user);
    } catch (err) {
      setError(err.message);
      if (authToken.length === 6) {
        setToken('');
        lastAutoSubmittedToken.current = '';
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenChange = (event) => {
    const nextToken = event.target.value.replace(/\D/g, '').slice(0, 6);
    setToken(nextToken);
    setError('');

    if (
      nextToken.length === 6 &&
      nextToken !== lastAutoSubmittedToken.current &&
      !isLoading &&
      email &&
      password
    ) {
      lastAutoSubmittedToken.current = nextToken;
      submit(null, nextToken);
    }

    if (nextToken.length < 6) {
      lastAutoSubmittedToken.current = '';
    }
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setResetStatus('');
    setResetError('');
  };

  const requestPasswordReset = async () => {
    setResetStatus('');
    setResetError('');

    if (!email) {
      setResetError('Enter your email address');
      return;
    }

    setIsResetSending(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to send password link');

      setResetStatus(data.message || 'Password setup link sent');
    } catch (err) {
      setResetError(err.message);
    } finally {
      setIsResetSending(false);
    }
  };

  const closeTotpOverlay = () => {
    setRequiresTotp(false);
    setSetup(null);
    setToken('');
    setError('');
    lastAutoSubmittedToken.current = '';
  };

  return (
    <>
      <form className="portal-panel auth-panel" onSubmit={submit}>
        <div className="tool-heading">
          <p>Protected Access</p>
          <h2>{portalName}</h2>
        </div>

        <label className="qr-field">
          <span>Email</span>
          <input value={email} onChange={handleEmailChange} type="email" />
        </label>

        <label className="qr-field">
          <span>Password</span>
          <input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type="password"
          />
        </label>

        <button className="primary-action" disabled={isLoading || !email || !password}>
          {isLoading ? 'Checking' : 'Sign In'}
        </button>
        {canResetPassword && (
          <>
            <button
              className="forgot-password-action"
              type="button"
              onClick={requestPasswordReset}
              disabled={isResetSending}
            >
              {isResetSending ? 'Sending password link' : 'Forgot password?'}
            </button>
            {resetStatus && <p className="form-ok">{resetStatus}</p>}
            {resetError && <p className="form-error">{resetError}</p>}
          </>
        )}
        {error && !requiresTotp && <p className="form-error">{error}</p>}
      </form>

      {requiresTotp && (
        <div className="modal-backdrop auth-modal-backdrop" role="presentation">
          <div
            className="modal-panel auth-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="authenticator-modal-title"
          >
            <div className="auth-modal-heading">
              <div>
                <p>Step-up verification</p>
                <h2 id="authenticator-modal-title">
                  {setup ? 'Set up your authenticator' : 'Enter authenticator code'}
                </h2>
                <span>
                  {setup
                    ? 'Scan the QR code, then enter the 6 digit code from your app.'
                    : 'Use the 6 digit code from your authenticator app to continue.'}
                </span>
              </div>
              <button type="button" className="auth-modal-close" onClick={closeTotpOverlay}>
                Back
              </button>
            </div>

            {setup && (
              <div className="totp-setup auth-totp-setup">
                <div className="auth-totp-brand">
                  <img src={logo} alt="CabSafe" />
                  <div>
                    <strong>CabSafe</strong>
                    <span>Authenticator enrolment</span>
                  </div>
                </div>
                <img className="totp-qr" src={setup.qr} alt="Authenticator setup QR code" />
                <p>Scan this in your authenticator app. It will appear as CabSafe.</p>
                <code>{setup.secret}</code>
              </div>
            )}

            <form className="auth-modal-form" onSubmit={submit}>
              <label className="qr-field auth-code-field">
                <span>Authenticator code</span>
                <input
                  value={token}
                  onChange={handleTokenChange}
                  className="auth-code-input auth-modal-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  autoFocus
                />
              </label>

              {canTrustDevice && (
                <label className="check-row auth-trust-row">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(event) => setTrustDevice(event.target.checked)}
                  />
                  <span>Trust this device for 7 days so you do not need a code each time.</span>
                </label>
              )}

              <button
                className="primary-action auth-modal-action"
                disabled={isLoading || token.length !== 6}
              >
                {isLoading ? 'Verifying' : 'Verify code'}
              </button>

              {error && <p className="form-error">{error}</p>}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

async function signOut(setUser) {
  await fetch('/api/auth/logout', { method: 'POST' });
  setUser(null);
}

function AdminPortal() {
  const [journeys, setJourneys] = React.useState([]);
  const [drivers, setDrivers] = React.useState([]);
  const [activeSection, setActiveSection] = React.useState('operations');
  const [driverSearch, setDriverSearch] = React.useState('');
  const [selectedDriverId, setSelectedDriverId] = React.useState('');
  const [bookingFilters, setBookingFilters] = React.useState({
    plate: '',
    badge: '',
    reg: '',
    date: '',
    time: '',
  });
  const [selectedBooking, setSelectedBooking] = React.useState(null);
  const [contextMenu, setContextMenu] = React.useState(null);
  const [emailForm, setEmailForm] = React.useState({ to: '', note: '' });
  const [emailStatus, setEmailStatus] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const [driverForm, setDriverForm] = React.useState({
    name: '',
    email: '',
  });
  const [driverResult, setDriverResult] = React.useState(null);
  const [driverError, setDriverError] = React.useState('');
  const [isCreatingDriver, setIsCreatingDriver] = React.useState(false);

  const loadDrivers = React.useCallback(() => {
    fetch('/api/users/drivers')
      .then((response) => (response.ok ? response.json() : { drivers: [] }))
      .then((data) => setDrivers(Array.isArray(data.drivers) ? data.drivers : []))
      .catch(() => setDrivers([]));
  }, []);

  const loadJourneys = React.useCallback((filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const query = params.toString();

    return fetch(`/api/journeys${query ? `?${query}` : ''}`)
      .then((response) => response.json())
      .then((data) => setJourneys(Array.isArray(data) ? data : []))
      .catch(() => setJourneys([]));
  }, []);

  React.useEffect(() => {
    loadJourneys();
  }, [loadJourneys]);

  React.useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  React.useEffect(() => {
    if (!drivers.length) {
      setSelectedDriverId('');
      return;
    }

    setSelectedDriverId((current) => {
      if (current && drivers.some((driver) => String(driver.id) === String(current))) return current;
      return String(drivers[0].id);
    });
  }, [drivers]);

  const updateDriverForm = (field, value) => {
    setDriverForm((current) => ({ ...current, [field]: value }));
    setDriverError('');
    setDriverResult(null);
  };

  const updateBookingFilter = (field, value) => {
    setBookingFilters((current) => ({ ...current, [field]: value }));
  };

  const lookupBookings = (event) => {
    event.preventDefault();
    loadJourneys(bookingFilters);
  };

  const clearBookingFilters = () => {
    const emptyFilters = { plate: '', badge: '', reg: '', date: '', time: '' };
    setBookingFilters(emptyFilters);
    loadJourneys(emptyFilters);
  };

  const createDriver = async (event) => {
    event.preventDefault();
    setDriverError('');
    setDriverResult(null);
    setIsCreatingDriver(true);

    try {
      const response = await fetch('/api/users/drivers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(driverForm),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to create driver');

      setDriverResult(data);
      setDrivers((current) => [data.driver, ...current]);
      setDriverForm({
        name: '',
        email: '',
      });
    } catch (err) {
      setDriverError(err.message);
    } finally {
      setIsCreatingDriver(false);
    }
  };

  const openBooking = (booking) => {
    setSelectedBooking(booking);
    setContextMenu(null);
    setEmailForm({ to: '', note: '' });
    setEmailStatus('');
    setEmailError('');
  };

  const showBookingMenu = (event, booking) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, booking });
  };

  const sendJourneyEmail = async (event) => {
    event.preventDefault();
    if (!selectedBooking) return;

    setEmailStatus('');
    setEmailError('');
    setIsSendingEmail(true);

    try {
      const response = await fetch(`/api/journeys/${selectedBooking.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to send journey email');
      setEmailStatus('Journey email sent');
      setEmailForm({ to: '', note: '' });
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const liveJourneys = journeys.filter((journey) => journey.status === 'active');
  const completedJourneys = journeys.filter((journey) => journey.status === 'completed');
  const filteredDrivers = drivers.filter((driver) => {
    const query = driverSearch.trim().toLowerCase();
    if (!query) return true;

    return [
      driver.name,
      driver.email,
      driver.badgeNumber,
      driver.plateNumber,
      driver.vehicleRegistration,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });
  const selectedDriver = drivers.find((driver) => String(driver.id) === String(selectedDriverId)) || null;
  const journeyMapSeed = journeys
    .map((journey) =>
      [
        journey.id || journey.public_ref || '',
        journey.updated_at || journey.start_time || '',
        journey.start_location || '',
        journey.start_lat ?? '',
        journey.start_lng ?? '',
      ].join(':')
    )
    .join('|');
  const journeyStartPoints = journeys
    .map((journey) => {
      const parsedStart = parsePoint(journey.start_location);
      const lat = toNumber(parsedStart?.lat ?? journey.start_lat ?? journey.current_lat);
      const lng = toNumber(parsedStart?.lng ?? journey.start_lng ?? journey.current_lng);
      if (lat === null || lng === null) return null;

      return {
        id: journey.id || journey.public_ref || `${lat}-${lng}`,
        lat,
        lng,
        label: journey.destination_text || journey.end_location || 'Journey destination pending',
        secondaryLabel: journey.start_location || formatPoint(lat, lng) || 'Start recorded',
        isLive: journey.status === 'active',
      };
    })
    .filter(Boolean);

  return (
    <>
      <div className="admin-shell">
        <header className="admin-topbar">
          <div className="admin-brand">
            <img src={logo} alt="CabSafe" />
            <span>Admin Portal</span>
          </div>
          <nav className="admin-menu" aria-label="Admin menu">
            {['operations', 'drivers', 'bookings'].map((section) => (
              <button
                key={section}
                type="button"
                className={activeSection === section ? 'active' : ''}
                onClick={() => setActiveSection(section)}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
          </nav>
        </header>

      {activeSection === 'operations' && (
      <div className="portal-panel">
        <div className="tool-heading">
          <p>Operations</p>
          <h2>Journey overview</h2>
        </div>
        <div className="metric-grid">
          <Metric label="Live journeys" value={String(liveJourneys.length)} />
          <Metric label="Completed journeys" value={String(completedJourneys.length)} />
          <Metric label="Total journeys" value={String(journeys.length)} />
        </div>
        <OperationsStartMap
          points={journeyStartPoints}
          totalJourneys={journeys.length}
          mapSeed={journeyMapSeed}
        />
      </div>
      )}

      {activeSection === 'drivers' && (
      <div className="portal-panel">
        <div className="tool-heading">
          <p>Drivers</p>
          <h2>Sign up new driver</h2>
        </div>
        <form className="driver-form" onSubmit={createDriver}>
          <label className="qr-field">
            <span>Name</span>
            <input
              value={driverForm.name}
              onChange={(event) => updateDriverForm('name', event.target.value)}
              autoComplete="name"
            />
          </label>
          <label className="qr-field">
            <span>Email</span>
            <input
              value={driverForm.email}
              onChange={(event) => updateDriverForm('email', event.target.value)}
              type="email"
              autoComplete="email"
            />
          </label>
          <button
            className="primary-action"
            disabled={isCreatingDriver || !driverForm.name || !driverForm.email}
          >
            {isCreatingDriver ? 'Sending Invite' : 'Send Driver Invite'}
          </button>
        </form>

        {driverResult && (
          <div className="form-success">
            <span>Invite email</span>
            <strong>{driverResult.emailSent ? 'Sent to driver' : 'Pending'}</strong>
          </div>
        )}
        {driverError && <p className="form-error">{driverError}</p>}

        <div className="driver-admin-layout">
          <div className="driver-directory">
            <label className="qr-field">
              <span>Find driver by badge number, name, or email</span>
              <input
                value={driverSearch}
                onChange={(event) => setDriverSearch(event.target.value)}
                placeholder="Search badge number"
              />
            </label>

            <div className="driver-picker-list">
              {filteredDrivers.map((driver) => (
                <button
                  key={driver.id}
                  type="button"
                  className={`driver-picker-card${String(driver.id) === String(selectedDriverId) ? ' active' : ''}`}
                  onClick={() => setSelectedDriverId(String(driver.id))}
                >
                  <span>{driver.name || 'Driver'}</span>
                  <strong>{driver.badgeNumber ? `Badge ${driver.badgeNumber}` : driver.email}</strong>
                  <small>
                    {driver.profileComplete ? 'Driver profile complete' : 'Driver profile pending'}
                  </small>
                  {driver.invitePending && <small>Password link sent</small>}
                </button>
              ))}

              {filteredDrivers.length === 0 && (
                <div className="booking-empty">
                  <span>Drivers</span>
                  <strong>No drivers match that search</strong>
                </div>
              )}
            </div>
          </div>

          <div className="portal-panel driver-card-panel">
            <div className="tool-heading">
              <p>Driver Card</p>
              <h2>{selectedDriver ? 'Selected driver' : 'Choose a driver'}</h2>
            </div>

            {selectedDriver ? (
              <>
                <div className="driver-card-toolbar">
                  <button
                    type="button"
                    className="secondary-action"
                    onClick={() => printDriverCard(selectedDriver)}
                  >
                    Print Driver Card
                  </button>
                </div>
                <div className="driver-print-panel">
                  <DriverCard driver={selectedDriver} />
                </div>
              </>
            ) : (
              <div className="booking-empty">
                <span>Driver card</span>
                <strong>Select a driver to preview and print their card</strong>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {activeSection === 'bookings' && (
      <div className="portal-panel">
        <div className="tool-heading">
          <p>Bookings</p>
          <h2>Lookup</h2>
        </div>
        <form className="lookup-form" onSubmit={lookupBookings}>
          <div className="lookup-grid">
            <label className="qr-field">
              <span>Plate</span>
              <input
                value={bookingFilters.plate}
                onChange={(event) => updateBookingFilter('plate', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Badge</span>
              <input
                value={bookingFilters.badge}
                onChange={(event) => updateBookingFilter('badge', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Reg</span>
              <input
                value={bookingFilters.reg}
                onChange={(event) => updateBookingFilter('reg', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Date</span>
              <input
                value={bookingFilters.date}
                onChange={(event) => updateBookingFilter('date', event.target.value)}
                type="date"
              />
            </label>
            <label className="qr-field">
              <span>Time</span>
              <input
                value={bookingFilters.time}
                onChange={(event) => updateBookingFilter('time', event.target.value)}
                type="time"
              />
            </label>
          </div>
          <div className="button-row">
            <button className="primary-action">Lookup</button>
            <button className="secondary-action" type="button" onClick={clearBookingFilters}>
              Clear
            </button>
          </div>
        </form>

        <div className="booking-list">
          {journeys.map((journey) => (
            <button
              key={journey.id || journey.public_ref}
              className="booking-row"
              type="button"
              onClick={() => openBooking(journey)}
              onContextMenu={(event) => showBookingMenu(event, journey)}
            >
              <span>{formatDateTime(journey.start_time)}</span>
              <strong>{journey.destination_text || journey.end_location || 'No destination yet'}</strong>
              <small>
                {[journey.driver_plate, journey.driver_badge, journey.driver_registration]
                  .filter(Boolean)
                  .join(' | ') || 'No vehicle details'}
              </small>
            </button>
          ))}
          {journeys.length === 0 && (
            <div className="booking-empty">
              <span>Bookings</span>
              <strong>No matches</strong>
            </div>
          )}
        </div>
      </div>
      )}
      </div>

      {contextMenu && (
        <div
          className="context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onMouseLeave={() => setContextMenu(null)}
        >
          <button type="button" onClick={() => openBooking(contextMenu.booking)}>
            View saved journey details
          </button>
        </div>
      )}

      {selectedBooking && (
        <div className="modal-backdrop" onClick={() => setSelectedBooking(null)}>
          <section className="modal-panel" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <div>
                <p>Saved Journey</p>
                <h2>{selectedBooking.public_ref || `#${selectedBooking.id}`}</h2>
              </div>
              <button type="button" onClick={() => setSelectedBooking(null)}>
                Close
              </button>
            </div>
            <JourneyDetails journey={selectedBooking} />
            <form className="email-form" onSubmit={sendJourneyEmail}>
              <label className="qr-field">
                <span>Email to</span>
                <input
                  value={emailForm.to}
                  onChange={(event) => setEmailForm((current) => ({ ...current, to: event.target.value }))}
                  type="email"
                />
              </label>
              <label className="qr-field">
                <span>Note</span>
                <input
                  value={emailForm.note}
                  onChange={(event) => setEmailForm((current) => ({ ...current, note: event.target.value }))}
                />
              </label>
              <button className="primary-action" disabled={isSendingEmail || !emailForm.to}>
                {isSendingEmail ? 'Sending' : 'Send Journey Email'}
              </button>
              {emailStatus && <p className="form-ok">{emailStatus}</p>}
              {emailError && <p className="form-error">{emailError}</p>}
            </form>
          </section>
        </div>
      )}
    </>
  );
}

function PasswordSetup() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [user, setUser] = React.useState(null);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!token) {
      setError('Password link missing');
      setIsLoading(false);
      return;
    }

    fetch(`/api/auth/password-setup/${encodeURIComponent(token)}`)
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!response.ok) throw new Error(data.error || 'Password link unavailable');
        setUser(data.user);
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [token]);

  const savePassword = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 10) {
      setError('Use at least 10 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch('/api/auth/password-setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to set password');

      window.location.href = data.redirectUrl || `/?email=${encodeURIComponent(user?.email || '')}`;
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Panel title="Password reset" eyebrow="CabSafe">Checking your secure link</Panel>;
  }

  if (error && !user) {
    return <Panel title="Password link unavailable" eyebrow="CabSafe">{error}</Panel>;
  }

  const portalLabel = user?.portalLabel || 'Portal';

  return (
    <form className="portal-panel auth-panel" onSubmit={savePassword}>
      <div className="tool-heading">
        <p>{portalLabel}</p>
        <h2>Create your password</h2>
      </div>
      <div className="form-success compact">
        <span>Account</span>
        <strong>{user?.email}</strong>
      </div>
      <label className="qr-field">
        <span>New password</span>
        <input
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          autoComplete="new-password"
        />
      </label>
      <label className="qr-field">
        <span>Confirm password</span>
        <input
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          type="password"
          autoComplete="new-password"
        />
      </label>
      <button className="primary-action" disabled={isSaving || !password || !confirmPassword}>
        {isSaving ? 'Saving' : 'Set Password'}
      </button>
      {error && <p className="form-error">{error}</p>}
    </form>
  );
}

function JourneyDetails({ journey }) {
  const rows = [
    ['Passenger', journey.passenger_phone || journey.passenger_id],
    ['Driver', journey.driver_name],
    ['Driver email', journey.driver_email],
    ['Plate', journey.driver_plate],
    ['Badge', journey.driver_badge],
    ['Reg', journey.driver_registration],
    ['Status', journey.status],
    ['Destination', journey.destination_text || journey.end_location],
    ['Start location', journey.start_location],
    ['Current location', formatPoint(journey.current_lat, journey.current_lng)],
    ['Started', formatDateTime(journey.start_time)],
    ['Updated', formatDateTime(journey.updated_at)],
    ['Share link', journey.shareUrl],
  ].filter(([, value]) => value !== undefined && value !== null && value !== '');

  return (
    <div className="details-grid">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

const emptyDriverProfile = {
  profilePhoto: '',
  licensingCouncil: '',
  badgeNumber: '',
  badgeExpiry: '',
  plateNumber: '',
  plateExpiry: '',
  vehicleRegistration: '',
  vehicleMake: '',
  vehicleColour: '',
};

function DriverPortal() {
  const [user, setUser] = React.useState(null);
  const [profileForm, setProfileForm] = React.useState(emptyDriverProfile);
  const [isProfileLoading, setIsProfileLoading] = React.useState(true);
  const [isProfileSaving, setIsProfileSaving] = React.useState(false);
  const [profileStatus, setProfileStatus] = React.useState('');
  const [profileError, setProfileError] = React.useState('');
  const isOnboardingInstall = new URLSearchParams(window.location.search).get('onboard') === 'install';

  React.useEffect(() => {
    fetch('/api/auth/me')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const nextUser = data?.user || null;
        setUser(nextUser);
        setProfileForm(toDriverProfile(nextUser));
      })
      .catch(() => setUser(null))
      .finally(() => setIsProfileLoading(false));
  }, []);

  const defaultUrl = user?.id
    ? `https://passenger.cabsafe.taxi/driver/${encodeURIComponent(user.id)}`
    : 'https://passenger.cabsafe.taxi';
  const profileComplete = isDriverProfileComplete(profileForm);
  const driverInitial = (user?.name || user?.email || 'Driver').trim().charAt(0).toUpperCase();

  const updateProfileForm = (field, value) => {
    setProfileForm((current) => ({ ...current, [field]: value }));
    setProfileStatus('');
    setProfileError('');
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setProfileStatus('');
    setProfileError('');
    setIsProfileSaving(true);

    try {
      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to save driver profile');

      setUser(data.user);
      setProfileForm(toDriverProfile(data.user));
      setProfileStatus('Driver profile saved');
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setProfileStatus('');
    setProfileError('');

    try {
      const preparedPhoto = await prepareDriverPhotoUpload(file);
      updateProfileForm('profilePhoto', preparedPhoto);
    } catch (error) {
      setProfileError(error.message || 'Unable to read picture');
    }
  };

  if (isProfileLoading) {
    return <Panel title="Driver Portal" eyebrow="Loading">Checking driver profile</Panel>;
  }

  return (
    <>
      <DriverInstallPanel isOnboarding={isOnboardingInstall} />
      <div className="portal-panel driver-profile-panel">
        <div className="tool-heading">
          <p>Driver Profile</p>
          <h2>{user?.name || 'Your details'}</h2>
        </div>

        <form className="driver-profile-form" onSubmit={saveProfile}>
          <div className="profile-photo-row">
            <div className="profile-photo-preview" aria-label="Driver picture preview">
              {profileForm.profilePhoto ? (
                <img src={profileForm.profilePhoto} alt="" />
              ) : (
                <span>{driverInitial}</span>
              )}
            </div>
            <label className="qr-field">
              <span>Picture (required)</span>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handlePhotoUpload} />
            </label>
          </div>

          <div className="lookup-grid">
            <label className="qr-field">
              <span>Licensing council</span>
              <input
                value={profileForm.licensingCouncil}
                onChange={(event) => updateProfileForm('licensingCouncil', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Badge number</span>
              <input
                value={profileForm.badgeNumber}
                onChange={(event) => updateProfileForm('badgeNumber', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Badge expiry</span>
              <input
                value={profileForm.badgeExpiry}
                onChange={(event) => updateProfileForm('badgeExpiry', event.target.value)}
                type="date"
              />
            </label>
            <label className="qr-field">
              <span>Plate number</span>
              <input
                value={profileForm.plateNumber}
                onChange={(event) => updateProfileForm('plateNumber', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Plate expiry</span>
              <input
                value={profileForm.plateExpiry}
                onChange={(event) => updateProfileForm('plateExpiry', event.target.value)}
                type="date"
              />
            </label>
            <label className="qr-field">
              <span>Vehicle reg</span>
              <input
                value={profileForm.vehicleRegistration}
                onChange={(event) => updateProfileForm('vehicleRegistration', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Make</span>
              <input
                value={profileForm.vehicleMake}
                onChange={(event) => updateProfileForm('vehicleMake', event.target.value)}
              />
            </label>
            <label className="qr-field">
              <span>Colour</span>
              <input
                value={profileForm.vehicleColour}
                onChange={(event) => updateProfileForm('vehicleColour', event.target.value)}
              />
            </label>
          </div>

          <button className="primary-action" disabled={isProfileSaving}>
            {isProfileSaving ? 'Saving' : 'Save Driver Profile'}
          </button>
          {profileStatus && <p className="form-ok">{profileStatus}</p>}
          {profileError && <p className="form-error">{profileError}</p>}
        </form>
      </div>

      {profileComplete ? (
        <>
          <QRGenerator defaultUrl={defaultUrl} driverName={user?.name || 'CabSafe Driver'} />
        </>
      ) : (
        <Panel title="Profile required" eyebrow="Driver QR">Complete your driver profile to activate QR tools.</Panel>
      )}
    </>
  );
}

function toDriverProfile(user) {
  if (!user) return emptyDriverProfile;

  return {
    profilePhoto: user.profilePhoto || '',
    licensingCouncil: user.licensingCouncil || '',
    badgeNumber: user.badgeNumber || '',
    badgeExpiry: user.badgeExpiry || '',
    plateNumber: user.plateNumber || '',
    plateExpiry: user.plateExpiry || '',
    vehicleRegistration: user.vehicleRegistration || '',
    vehicleMake: user.vehicleMake || '',
    vehicleColour: user.vehicleColour || user.vehicleColor || '',
  };
}

function DriverInstallPanel({ isOnboarding }) {
  return (
    <div className={`portal-panel pwa-install-panel${isOnboarding ? ' onboarding' : ''}`}>
      <div className="tool-heading">
        <p>Driver App</p>
        <h2>Install CabSafe Driver</h2>
      </div>
      <p className="install-copy">
        Add CabSafe to your phone or tablet for quicker access to your driver QR tools.
      </p>
      <InstallPrompt label="Install Driver App" showFallback />
    </div>
  );
}

function isDriverProfileComplete(profile) {
  return Boolean(
    profile.profilePhoto &&
    profile.licensingCouncil &&
    profile.badgeNumber &&
    profile.badgeExpiry &&
    profile.plateNumber &&
    profile.plateExpiry &&
    profile.vehicleRegistration &&
    profile.vehicleMake &&
    profile.vehicleColour
  );
}

function PassengerPortal() {
  const publicRef = getJourneyRefFromPath();
  if (publicRef) return <SharedJourneyView publicRef={publicRef} />;

  const driverProfileId = getDriverProfileIdFromPath();
  if (driverProfileId) return <PublicDriverProfile driverId={driverProfileId} />;

  return (
    <>
      <PassengerJourneyCreator />
      <InstallPrompt label="Install Passenger App" />
    </>
  );
}

function PublicDriverProfile({ driverId }) {
  const [driver, setDriver] = React.useState(null);
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    setIsLoading(true);
    setError('');

    fetch(`/api/passengers/drivers/${encodeURIComponent(driverId)}`)
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!response.ok) throw new Error(data.error || 'Driver profile not found');
        setDriver(data.driver);
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [driverId]);

  if (isLoading) {
    return <Panel title="Driver profile" eyebrow="CabSafe">Loading driver details</Panel>;
  }

  if (error) {
    return <Panel title="Driver unavailable" eyebrow="CabSafe">{error}</Panel>;
  }

  const initial = (driver.name || 'Driver').trim().charAt(0).toUpperCase();

  return (
    <>
      <div className="portal-panel public-driver-profile">
        <div className="public-driver-brand" aria-label="CabSafe branding">
          <img className="public-driver-brand-logo" src={logo} alt="CabSafe" />
          <div className="public-driver-brand-copy">
            <span>Verified journey sharing</span>
            <strong>CabSafe driver card</strong>
          </div>
        </div>
        <DriverCard driver={driver} initial={initial} />
      </div>
      <PassengerJourneyCreator initialDriverId={driverId} />
      <InstallPrompt label="Install Passenger App" />
    </>
  );
}

function DriverCard({ driver, initial }) {
  const safeDriver = driver || {};
  const fallbackInitial = (safeDriver.name || 'Driver').trim().charAt(0).toUpperCase();
  const vehicleSummary = [safeDriver.vehicleColour, safeDriver.vehicleMake].filter(Boolean).join(' ');
  const councilLogoUrl = getCouncilLogoUrl(safeDriver.licensingCouncil);
  const detailRows = [
    [
      safeDriver.badgeNumber ? `Badge ${safeDriver.badgeNumber}` : '',
      safeDriver.plateNumber ? `Plate ${safeDriver.plateNumber}` : '',
    ].filter(Boolean),
    [
      safeDriver.vehicleRegistration ? `Reg ${safeDriver.vehicleRegistration}` : '',
      vehicleSummary,
    ].filter(Boolean),
    [
      safeDriver.badgeExpiry ? `Badge exp ${safeDriver.badgeExpiry}` : '',
      safeDriver.plateExpiry ? `Plate exp ${safeDriver.plateExpiry}` : '',
    ].filter(Boolean),
  ].filter((row) => row.length > 0);

  return (
    <div className="public-driver-card" aria-label="Driver details">
      <div className="public-driver-photo" aria-label="Driver picture">
        {safeDriver.profilePhoto ? <img src={safeDriver.profilePhoto} alt="" /> : <span>{initial || fallbackInitial}</span>}
      </div>
      <div className="public-driver-main">
        <span>Licensed driver</span>
        <strong>{safeDriver.name || 'CabSafe Driver'}</strong>
        {safeDriver.licensingCouncil && <small>{safeDriver.licensingCouncil}</small>}
        {councilLogoUrl && (
          <div className="driver-council-logo-wrap">
            <img
              className="driver-council-logo"
              src={councilLogoUrl}
              alt={`${safeDriver.licensingCouncil || 'Council'} logo`}
            />
          </div>
        )}
        <div className="driver-card-lines">
          {detailRows.map((row, rowIndex) => (
            <p key={`${safeDriver.id || safeDriver.name || 'driver'}-${rowIndex}`}>
              {row.map((detail) => (
                <span key={detail}>{detail}</span>
              ))}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

function PassengerJourneyCreator({ initialDriverId = '' }) {
  const [phoneNumber, setPhoneNumber] = React.useState(() => getSavedPassengerPhone());
  const [driverId, setDriverId] = React.useState(() => initialDriverId || getDriverIdFromLocation());
  const [profile, setProfile] = React.useState(null);
  const [destinationText, setDestinationText] = React.useState('');
  const [destinationPlaceId, setDestinationPlaceId] = React.useState('');
  const [destinationLat, setDestinationLat] = React.useState(null);
  const [destinationLng, setDestinationLng] = React.useState(null);
  const [destinationSuggestions, setDestinationSuggestions] = React.useState([]);
  const [isDestinationLoading, setIsDestinationLoading] = React.useState(false);
  const [currentLocation, setCurrentLocation] = React.useState(null);
  const [saveHome, setSaveHome] = React.useState(false);
  const [shareUrl, setShareUrl] = React.useState('');
  const [activeJourneyRef, setActiveJourneyRef] = React.useState('');
  const [error, setError] = React.useState('');

  const detectCurrentLocation = React.useCallback((showError = false) => {
    if (!navigator.geolocation) {
      if (showError) setError('Location is not available on this device.');
      return;
    }

    if (showError) setError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        if (showError) setError('Location access was not allowed.');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }, []);

  React.useEffect(() => {
    if (initialDriverId) setDriverId(initialDriverId);
  }, [initialDriverId]);

  React.useEffect(() => {
    detectCurrentLocation(false);
  }, [detectCurrentLocation]);
  useJourneyLocationReporter(activeJourneyRef, phoneNumber, Boolean(activeJourneyRef), (journeyUpdate) => {
    if (journeyUpdate?.status === 'completed') setActiveJourneyRef('');
  });

  React.useEffect(() => {
    if (!phoneNumber) {
      setProfile(null);
      return;
    }

    savePassengerPhone(phoneNumber);
    fetch(`/api/passengers/profile?phone=${encodeURIComponent(phoneNumber)}`)
      .then((response) => response.json())
      .then((data) => setProfile(data.profile))
      .catch(() => setProfile(null));
  }, [phoneNumber]);

  React.useEffect(() => {
    const query = destinationText.trim();
    if (query.length < 3 || destinationPlaceId) {
      setDestinationSuggestions([]);
      setIsDestinationLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ input: query });
      if (currentLocation) {
        params.set('lat', currentLocation.lat);
        params.set('lng', currentLocation.lng);
      }

      setIsDestinationLoading(true);
      fetch(`/api/passengers/places?${params}`, { signal: controller.signal })
        .then((response) => (response.ok ? response.json() : { suggestions: [] }))
        .then((data) => {
          setDestinationSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') setDestinationSuggestions([]);
        })
        .finally(() => setIsDestinationLoading(false));
    }, 240);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [destinationText, destinationPlaceId, currentLocation]);

  const locate = () => detectCurrentLocation(true);

  const useHome = () => {
    if (!profile?.home_address) return;
    setDestinationText(profile.home_address);
    setDestinationPlaceId('');
    setDestinationLat(profile.home_lat ?? null);
    setDestinationLng(profile.home_lng ?? null);
    setDestinationSuggestions([]);
  };

  const updateDestinationText = (value) => {
    setDestinationText(value);
    setDestinationPlaceId('');
    setDestinationLat(null);
    setDestinationLng(null);
  };

  const selectDestinationSuggestion = async (suggestion) => {
    setDestinationText(suggestion.text);
    setDestinationPlaceId(suggestion.placeId);
    setDestinationSuggestions([]);
    setIsDestinationLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/passengers/places/${encodeURIComponent(suggestion.placeId)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load address');

      setDestinationText(data.place.text || suggestion.text);
      setDestinationLat(data.place.lat);
      setDestinationLng(data.place.lng);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDestinationLoading(false);
    }
  };

  const createJourney = async () => {
    setError('');
    setShareUrl('');
    setActiveJourneyRef('');

    try {
      const response = await fetch('/api/journeys/passenger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber,
          destinationText,
          destinationLat,
          destinationLng,
          currentLat: currentLocation?.lat,
          currentLng: currentLocation?.lng,
          saveHome,
          homeAddress: destinationText,
          homeLat: destinationLat,
          homeLng: destinationLng,
          driverId,
        }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to create journey');
      setShareUrl(data.shareUrl);
      setActiveJourneyRef(data.public_ref || extractJourneyRefFromShareUrl(data.shareUrl));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="portal-panel">
      <div className="tool-heading">
        <p>Passenger Journey</p>
        <h2>Start and share your trip</h2>
      </div>

      <label className="qr-field">
        <span>Mobile number</span>
        <input
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
          inputMode="tel"
          autoComplete="tel"
          placeholder="Your mobile number"
        />
      </label>

      <button className="secondary-action" onClick={locate}>
        Use My Current Location
      </button>

      {currentLocation && <MapPreview point={currentLocation} label="Your current location" />}

      <label className="qr-field">
        <span>Destination</span>
        <input
          value={destinationText}
          onChange={(event) => updateDestinationText(event.target.value)}
          autoComplete="street-address"
          placeholder="Where are you going?"
        />
      </label>
      {(isDestinationLoading || destinationSuggestions.length > 0) && (
        <div className="address-suggestions">
          {destinationSuggestions.map((suggestion) => (
            <button
              type="button"
              key={suggestion.placeId}
              onClick={() => selectDestinationSuggestion(suggestion)}
            >
              <strong>{suggestion.mainText || suggestion.text}</strong>
              {suggestion.secondaryText && <span>{suggestion.secondaryText}</span>}
              {suggestion.distanceMeters !== null && (
                <small>{formatDistance(suggestion.distanceMeters)}</small>
              )}
            </button>
          ))}
          {isDestinationLoading && <p>Loading nearby addresses</p>}
          {destinationSuggestions.length > 0 && (
            <small className="places-attribution">Powered by Google</small>
          )}
        </div>
      )}

      {profile?.home_address && (
        <button className="secondary-action" onClick={useHome}>
          Use Saved Home
        </button>
      )}

      <label className="check-row">
        <input
          type="checkbox"
          checked={saveHome}
          onChange={(event) => setSaveHome(event.target.checked)}
        />
        Save this destination as home for this phone number
      </label>

      <button
        className="primary-action"
        onClick={createJourney}
        disabled={!phoneNumber || !destinationText}
      >
        Create Share Link
      </button>

      {shareUrl && <SharePanel shareUrl={shareUrl} />}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}

function SharedJourneyView({ publicRef }) {
  const [journey, setJourney] = React.useState(null);
  const [route, setRoute] = React.useState(null);
  const [routeError, setRouteError] = React.useState('');
  const [error, setError] = React.useState('');
  const [phoneNumber] = React.useState(() => getSavedPassengerPhone());

  React.useEffect(() => {
    const load = () => {
      fetch(`/api/journeys/public/${publicRef}`)
        .then((response) => response.json().then((data) => ({ response, data })))
        .then(({ response, data }) => {
          if (!response.ok) throw new Error(data.error || 'Journey not found');
          setJourney(data.journey);
          if (data.journey?.status === 'completed') {
            setRoute(null);
            setRouteError('');
            return null;
          }

          return fetch(`/api/journeys/public/${publicRef}/route`)
            .then((routeResponse) => routeResponse.json().then((routeData) => ({ routeResponse, routeData })))
            .then(({ routeResponse, routeData }) => {
              if (!routeResponse.ok) throw new Error(routeData.error || 'Route unavailable');
              setRoute(routeData.route);
              setRouteError('');
            });
        })
        .catch((err) => {
          if (err.message === 'Journey not found') {
            setError(err.message);
          } else {
            setRouteError(err.message);
          }
        });
    };

    load();
    const timer = window.setInterval(load, 20000);
    return () => window.clearInterval(timer);
  }, [publicRef]);

  const isActivePassengerJourney = journey && journey.status !== 'completed';
  useJourneyLocationReporter(publicRef, phoneNumber, Boolean(isActivePassengerJourney), setJourney);

  if (error) return <Panel title="Journey unavailable" eyebrow="Shared Journey">{error}</Panel>;
  if (!journey) return <Panel title="Loading journey" eyebrow="Shared Journey">Checking trip status</Panel>;

  const destinationReached = journey.status === 'completed';

  return (
    <>
      <div className="portal-panel shared-route-panel">
        <div className="tool-heading">
          <p>Shared Journey</p>
          <h2>{destinationReached ? 'Destination reached' : 'Live trip view'}</h2>
        </div>

        {destinationReached ? (
          <div className="destination-reached">
            <strong>Destination reached</strong>
            <span>{journey.destination_text || 'The passenger has arrived.'}</span>
          </div>
        ) : (
          <>
            <div className="route-summary">
              <div>
                <span>ETA estimate</span>
                <strong>{route?.durationText || 'Calculating'}</strong>
                {route?.durationSeconds && <small>{formatEta(route.durationSeconds)}</small>}
              </div>
              <div>
                <span>Remaining</span>
                <strong>{route?.distanceText || 'Waiting for route'}</strong>
              </div>
              <div>
                <span>Destination</span>
                <strong>{journey.destination_text || 'Not supplied'}</strong>
              </div>
            </div>

            {route?.hasMap && route.mapFrameUrl ? (
              <iframe
                className="locked-route-map"
                src={route.mapFrameUrl}
                title="Live route from vehicle location to destination"
                scrolling="no"
              />
            ) : (
              <div className="route-map-placeholder">
                {routeError || 'Waiting for passenger location and destination route.'}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function useJourneyLocationReporter(publicRef, phoneNumber, enabled, onJourneyUpdate) {
  const lastLocationSent = React.useRef(0);

  React.useEffect(() => {
    if (!publicRef || !phoneNumber || !enabled || !navigator.geolocation) return undefined;

    const sendPosition = (position) => {
      const now = Date.now();
      if (now - lastLocationSent.current < 15000) return;
      lastLocationSent.current = now;

      fetch(`/api/journeys/public/${publicRef}/location`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber,
          currentLat: position.coords.latitude,
          currentLng: position.coords.longitude,
        }),
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (data && onJourneyUpdate) onJourneyUpdate(data);
        })
        .catch(() => {});
    };

    const watchId = navigator.geolocation.watchPosition(sendPosition, () => {}, {
      enableHighAccuracy: true,
      maximumAge: 10000,
      timeout: 15000,
    });

    return () => navigator.geolocation.clearWatch(watchId);
  }, [publicRef, phoneNumber, enabled, onJourneyUpdate]);
}

function SharePanel({ shareUrl }) {
  const text = encodeURIComponent(`Track my CabSafe journey: ${shareUrl}`);

  return (
    <div className="share-panel">
      <strong>{shareUrl}</strong>
      <div className="share-actions">
        <a href={`sms:?&body=${text}`}>SMS</a>
        <a href={`https://wa.me/?text=${text}`} target="_blank" rel="noreferrer">WhatsApp</a>
        <a href={`fb-messenger://share/?link=${encodeURIComponent(shareUrl)}`}>Messenger</a>
      </div>
    </div>
  );
}

function MapPreview({ point, label }) {
  const delta = 0.01;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${point.lng - delta},${point.lat - delta},${point.lng + delta},${point.lat + delta}&layer=mapnik&marker=${point.lat},${point.lng}`;

  return (
    <div className="map-preview">
      <span>{label}</span>
      <iframe title={label} src={src} />
    </div>
  );
}

function OperationsStartMap({ points, totalJourneys, mapSeed }) {
  const mapVersion = mapSeed || points
    .map((point) => `${point.id}:${point.lat}:${point.lng}:${point.isLive ? 1 : 0}`)
    .join('|');
  const mapFrameUrl = `/api/journeys/admin-start-map-frame?v=${encodeURIComponent(mapVersion)}`;

  return (
    <section className="operations-map-panel" aria-labelledby="operations-map-title">
      <div className="operations-map-header">
        <div>
          <span>Journey start map</span>
          <h3 id="operations-map-title">Where journeys are beginning</h3>
        </div>
        <strong>
          {points.length} of {totalJourneys} mapped
        </strong>
      </div>

      {totalJourneys > 0 ? (
        <>
          <div className="operations-map-frame">
            <iframe
              className="operations-map-canvas operations-map-frame-view"
              src={mapFrameUrl}
              title="Interactive map showing where journeys are beginning"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className="operations-map-legend">
            <span><i className="live" />Live start</span>
            <span><i />Completed start</span>
          </div>

          <div className="operations-map-list">
            {points.slice(0, 8).map((point) => (
              <div key={point.id}>
                <span>{point.secondaryLabel}</span>
                <strong>{point.label}</strong>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="booking-empty operations-map-empty">
          <span>Map status</span>
          <strong>No journeys available to map yet</strong>
        </div>
      )}
    </section>
  );
}

function InstallPrompt({ label, showFallback = false }) {
  const [promptEvent, setPromptEvent] = React.useState(null);
  const [isInstalled, setIsInstalled] = React.useState(false);
  const [canPrompt, setCanPrompt] = React.useState(false);

  React.useEffect(() => {
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsInstalled(standalone);

    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setPromptEvent(event);
      setCanPrompt(true);
    };
    const handleInstalled = () => {
      setIsInstalled(true);
      setPromptEvent(null);
      setCanPrompt(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  if (isInstalled) {
    return showFallback ? (
      <p className="install-status">CabSafe Driver is installed on this device.</p>
    ) : null;
  }

  const handleInstall = async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    await promptEvent.userChoice;
    setPromptEvent(null);
    setCanPrompt(false);
  };

  if (canPrompt) {
    return (
      <button className="secondary-action" onClick={handleInstall}>
        {label}
      </button>
    );
  }

  return showFallback ? (
    <p className="install-status">
      Use your browser menu to install CabSafe Driver as an app when prompted by your device.
    </p>
  ) : null;
}

function Metric({ label, value }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Panel({ eyebrow, title, children }) {
  return (
    <div className="portal-panel">
      <div className="tool-heading">
        <p>{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <p className="panel-copy">{children}</p>
    </div>
  );
}

function getJourneyRefFromPath() {
  const match = window.location.pathname.match(/^\/journey(.+)\.html$/i);
  return match ? match[1] : '';
}

function extractJourneyRefFromShareUrl(value) {
  try {
    const url = new URL(value);
    const match = url.pathname.match(/^\/journey(.+)\.html$/i);
    return match ? match[1] : '';
  } catch {
    const match = String(value || '').match(/journey([A-Za-z0-9_-]+)\.html/i);
    return match ? match[1] : '';
  }
}

function getDriverProfileIdFromPath() {
  const match = window.location.pathname.match(/^\/driver\/([^/]+)\/?$/i);
  return match ? decodeURIComponent(match[1]) : '';
}

function isPasswordSetupPath() {
  return window.location.pathname === '/set-password';
}

function isLocalAuthoritiesPath() {
  return window.location.pathname.replace(/\/+$/, '') === '/local-authorities';
}

function isHackneyDriversPath() {
  return window.location.pathname.replace(/\/+$/, '') === '/hackney-drivers';
}

function getDriverIdFromLocation() {
  return getDriverProfileIdFromPath() || new URLSearchParams(window.location.search).get('driver') || '';
}

function formatDateTime(value) {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPoint(lat, lng) {
  if (lat === undefined || lat === null || lng === undefined || lng === null) return '';
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
}

function parsePoint(value) {
  const text = String(value || '').trim();
  const match = text.match(
    /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/
  );
  if (!match) return null;

  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { lat, lng };
}

function toNumber(value) {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatDistance(meters) {
  const value = Number(meters);
  if (!Number.isFinite(value)) return '';
  if (value < 1000) return `${Math.round(value)}m away`;
  return `${(value / 1000).toFixed(value < 10000 ? 1 : 0)}km away`;
}

function formatEta(secondsFromNow) {
  const seconds = Number(secondsFromNow);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';

  const eta = new Date(Date.now() + seconds * 1000);
  return eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function savePassengerPhone(value) {
  const phone = String(value || '').trim();
  if (!phone) return;

  setCookie('cabsafe_phone', phone, 365);
  try {
    window.localStorage.setItem('cabsafe_phone', phone);
  } catch {
    // Cookie storage is enough when localStorage is unavailable.
  }
}

function getSavedPassengerPhone() {
  const fromCookie = getCookie('cabsafe_phone');
  if (fromCookie) return fromCookie;

  try {
    return window.localStorage.getItem('cabsafe_phone') || '';
  } catch {
    return '';
  }
}

function setCookie(name, value, days) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
}

function getCookie(name) {
  const row = document.cookie.split('; ').find((item) => item.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.split('=')[1]) : '';
}

function printDriverCard(driver) {
  if (!driver) return;
  const driverUrl = `https://passenger.cabsafe.taxi/driver/${encodeURIComponent(driver.id)}`;
  const printUrl = `/api/qr/print?url=${encodeURIComponent(driverUrl)}&name=${encodeURIComponent(driver.name || 'CabSafe Driver')}`;
  window.open(printUrl, '_blank', 'noopener,noreferrer');
}

function getCouncilLogoUrl(council) {
  const key = String(council || '').trim().toLowerCase();
  if (!key) return '';

  const logos = {
    sefton: 'https://www.sefton.gov.uk/media/pelbaktc/seftoncouncil-logo-v2-cmyk.png?rmode=max&width=500',
    'sefton council': 'https://www.sefton.gov.uk/media/pelbaktc/seftoncouncil-logo-v2-cmyk.png?rmode=max&width=500',
  };

  return logos[key] || '';
}

export default App;
